import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSegments } from 'expo-router';
import { getPrototypeSession } from '../../utils/prototypeSession';
import { getAuthSessionState, subscribeAuthSession } from '../../utils/authSession';
import type { PrototypeSession } from '../../types/prototypeSession';

const publicRouteSegments = new Set(['login', 'onboarding', 'grade-select']);

const getTopSegment = (segments: string[]): string | null =>
  typeof segments[0] === 'string' ? segments[0] : null;

export function PrototypeAuthGate() {
  const router = useRouter();
  const pathname = usePathname();
  const segments = useSegments();
  const [session, setSession] = useState<PrototypeSession | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    return subscribeAuthSession((nextState) => {
      setAuthenticated(nextState.isAuthenticated);
    });
  }, []);

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      const [nextSession, authSession] = await Promise.all([getPrototypeSession(), getAuthSessionState()]);
      if (!active) {
        return;
      }
      setSession(nextSession);
      setAuthenticated(authSession.isAuthenticated);
      setLoaded(true);
    };

    void loadSession();

    return () => {
      active = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (!loaded || !session) {
      return;
    }

    const topSegment = getTopSegment(segments as string[]);
    const isPublicRoute = topSegment ? publicRouteSegments.has(topSegment) : false;

    if (!authenticated && !isPublicRoute) {
      router.replace('/login');
      return;
    }

    if (
      authenticated &&
      !session.onboardingComplete &&
      topSegment !== 'onboarding' &&
      topSegment !== 'grade-select'
    ) {
      router.replace('/onboarding');
      return;
    }

    if (authenticated && session.onboardingComplete && isPublicRoute) {
      router.replace('/');
    }
  }, [authenticated, loaded, router, segments, session]);

  return null;
}
